import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BadgeDollarSign, Loader2, Save, Check, X, TrendingUp } from "lucide-react";

export default function RedemptionDesk() {
  const [data, setData] = useState(null);
  const [rate, setRate] = useState("");
  const [minBlc, setMinBlc] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => { api.get("/admin/redemption").then(({ data: d }) => {
    setData(d); setRate(String(d.config.rate_usd)); setMinBlc(String(d.config.min_blc));
  }).catch(() => {}); };
  useEffect(load, []);

  const save = async (enabledOverride) => {
    setBusy("save");
    try {
      const body = { rate_usd: Number(rate) || 0, min_blc: Number(minBlc) || 25 };
      if (enabledOverride != null) body.enabled = enabledOverride;
      await api.put("/admin/redemption/settings", body);
      toast.success(enabledOverride === false ? "Redemption Portal closed"
        : `Today's rate is live — $${rate} per BLC${enabledOverride ? " (portal OPEN)" : ""}. Players have been notified.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const decide = async (r, approve) => {
    setBusy(r.id);
    try {
      await api.post(`/admin/redemption/${r.id}/decide`, { approve });
      toast.success(approve
        ? `Approved — ${r.user_name} gets $${r.usd.toFixed(2)} ${r.method === "credit" ? "store credit" : "cash (pay them directly)"}`
        : `Denied — ${r.blc} BLC returned to ${r.user_name}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const { config, pending, decided, totals } = data;

  return (
    <div className="space-y-5" data-testid="redemption-desk">
      <p className="text-xs text-zinc-500">
        Set today's cash value for 1 BLC — the player Redemption Portal (in their Wallet) shows it live, and every rate change
        posts to the feed and notifies all members. Approving a store-credit request adds the dollars instantly; cash requests
        you pay in person, then mark PAID. Denying returns the coins.
      </p>
      <div className={`border p-4 ${config.enabled ? "border-emerald-500/50 bg-emerald-500/5" : "border-pb-border bg-pb-elevated/40"}`}>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-bold">Today's rate ($ per BLC)</label>
            <input data-testid="redemption-rate-input" type="number" min={0} step={0.01} value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="w-28 bg-pb-elevated border border-pb-border px-3 py-2.5 text-white font-mono text-sm focus:outline-none focus:border-amber-400" />
          </div>
          <div>
            <label className="block text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-bold">Min BLC</label>
            <input data-testid="redemption-min-input" type="number" min={1} step={1} value={minBlc}
              onChange={(e) => setMinBlc(e.target.value)}
              className="w-20 bg-pb-elevated border border-pb-border px-3 py-2.5 text-white font-mono text-sm focus:outline-none focus:border-amber-400" />
          </div>
          <button data-testid="redemption-save-rate" onClick={() => save(null)} disabled={!!busy}
            className="flex items-center gap-1.5 bg-amber-400 text-black font-black uppercase tracking-widest px-5 py-3 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Set Today's Rate
          </button>
          <button data-testid="redemption-toggle" onClick={() => save(!config.enabled)} disabled={!!busy}
            className={`flex items-center gap-1.5 font-black uppercase tracking-widest px-5 py-3 text-xs border transition-colors disabled:opacity-50 ${
              config.enabled ? "border-red-500/60 text-red-400 hover:bg-red-500/10" : "border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10"}`}>
            {config.enabled ? "Close Portal" : "Open Portal"}
          </button>
          <div className="text-[10px] uppercase tracking-widest font-black" data-testid="redemption-status">
            {config.enabled ? <span className="text-emerald-400">PORTAL OPEN</span> : <span className="text-zinc-500">PORTAL CLOSED</span>}
          </div>
        </div>
        {(config.history || []).length > 0 && (
          <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="redemption-rate-history">
            <TrendingUp className="w-3 h-3 text-zinc-600" />
            {config.history.slice(-8).map((h, i) => (
              <span key={i} className="text-[9px] font-mono text-zinc-500">
                ${h.rate}<span className="text-zinc-700"> · {new Date(h.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["Pending Requests", totals.pending_count, "#FACC15"],
          ["Pending Value", `$${totals.pending_usd.toFixed(2)}`, "#FACC15"],
          ["Total Paid Out", `$${totals.paid_usd.toFixed(2)}`, "#4ADE80"]].map(([l, v, c]) => (
          <div key={l} className="bg-pb-elevated border border-pb-border p-3">
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{l}</div>
            <div className="font-display font-black text-lg" style={{ color: c }}>{v}</div>
          </div>
        ))}
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Pending requests</div>
        <div className="divide-y divide-pb-border/40 border border-pb-border" data-testid="redemption-pending">
          {pending.length === 0 && <div className="p-3 text-xs text-zinc-600">No pending requests.</div>}
          {pending.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5" data-testid={`redemption-pending-${r.id}`}>
              <div className="text-xs">
                <span className="font-semibold text-white">{r.user_name}</span>
                <span className="text-zinc-400"> — {r.blc} BLC → </span>
                <span className="text-amber-400 font-mono font-bold">${r.usd.toFixed(2)}</span>
                <span className="text-[9px] uppercase tracking-widest ml-2 font-black" style={{ color: r.method === "credit" ? "#FACC15" : "#4ADE80" }}>
                  {r.method === "credit" ? "Store Credit" : "Cash"}
                </span>
              </div>
              <div className="flex gap-1.5">
                <button data-testid={`redemption-approve-${r.id}`} onClick={() => decide(r, true)} disabled={!!busy}
                  className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-black px-3 py-2 hover:bg-[#BEF264] transition-colors disabled:opacity-50">
                  {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                </button>
                <button data-testid={`redemption-deny-${r.id}`} onClick={() => decide(r, false)} disabled={!!busy}
                  className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest border border-red-500/60 text-red-400 px-3 py-2 hover:bg-red-500/10 transition-colors disabled:opacity-50">
                  <X className="w-3 h-3" /> Deny
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {decided.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Recent decisions</div>
          <div className="divide-y divide-pb-border/40 border border-pb-border max-h-48 overflow-y-auto">
            {decided.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-3 py-2 text-xs">
                <span className="text-zinc-400">{r.user_name} — {r.blc} BLC (${r.usd.toFixed(2)} {r.method})</span>
                <span className="text-[9px] font-black uppercase tracking-widest"
                  style={{ color: r.status === "paid" ? "#4ADE80" : "#F87171" }}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
